import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  HR_KEYS,
  hrErrorData,
  hrErrorMessage,
  hrErrorStatus,
  hrGet,
  hrPost,
} from "../../api/hrApi";
import type { HrPunchType } from "../../types/hr.model";
import type {
  HrPunchInput,
  HrPunchResponse,
  HrPunchState,
} from "../../types/hr.contract";
import { fmtClockCompact, fmtHours } from "../../utils/hrFormat";
import { markManualCheckOut } from "@/features/checkIn/components/checkInSession";
import { HR_ATTENDANCE_KEYS } from "./attendance.keys";
import { newIdempotencyKey } from "./hrQuery";

/**
 * GET /attendance/punch-state — the caller's own punch button state.
 * `placeholder` (e.g. `me.punch` from the session bootstrap) lets the button
 * render immediately; the live state replaces it as soon as it lands.
 */
export function usePunchState(options: { enabled?: boolean; placeholder?: HrPunchState | null } = {}) {
  return useQuery({
    queryKey: HR_ATTENDANCE_KEYS.punchState,
    queryFn: () => hrGet<HrPunchState>("/attendance/punch-state"),
    enabled: options.enabled ?? true,
    placeholderData: options.placeholder ?? undefined,
    staleTime: 60_000,
    // A punch changes the day under the button; keep it honest if the tab is
    // left open (it is cheap — one row, no lists).
    refetchInterval: 5 * 60_000,
  });
}

const PUNCH_TOAST: Record<HrPunchType, (res: HrPunchResponse) => string> = {
  IN: (res) =>
    res.session.checkInMinuteIST != null
      ? `Checked in at ${fmtClockCompact(res.session.checkInMinuteIST)}`
      : "Checked in",
  OUT: (res) =>
    `Checked out${
      res.session.checkOutMinuteIST != null
        ? ` at ${fmtClockCompact(res.session.checkOutMinuteIST)}`
        : ""
    } · ${fmtHours(res.punchState.workedTodayMinutes)} today`,
  BREAK_START: () => "Break started",
  BREAK_END: () => "Break ended — welcome back",
};

/**
 * POST /attendance/punch. Call `punch(type)` from a click handler: every
 * click mints a NEW idempotency key (a replay of the same key returns the
 * original result, so a double-submit can never double-count), and the
 * caller disables the button while `isPending`.
 */
export function usePunch() {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationKey: ["hr", "attendance", "punch"],
    mutationFn: (input: HrPunchInput) =>
      hrPost<HrPunchResponse>("/attendance/punch", input),
    onSuccess: (res, input) => {
      if (res?.punchState) {
        qc.setQueryData(HR_ATTENDANCE_KEYS.punchState, res.punchState);
      }
      // Their own check-out also ends lead availability (the server mirrors
      // it): the "Please check in first" prompt must not come straight back.
      if (input.type === "OUT") markManualCheckOut();
      toast.success(PUNCH_TOAST[input.type](res));
      // Attendance was recorded, but leads did not start (outside working
      // hours, a location rule) — the server says why.
      const notice = res?.punchState?.presence?.notice;
      if (notice) toast.info(notice, { duration: 8000 });
    },
    onError: (err) => {
      // A 409 (state moved under the button — another tab, the mobile app, an
      // auto-close) carries the CURRENT state: show it at once.
      const current = hrErrorData<{ punchState?: HrPunchState }>(err)?.punchState;
      if (hrErrorStatus(err) === 409 && current) {
        qc.setQueryData(HR_ATTENDANCE_KEYS.punchState, current);
      }
      toast.error(hrErrorMessage(err, "Couldn't record that — please try again"));
    },
    onSettled: () => {
      // It moves the register, the sessions, the tiles and the dashboard —
      // and lead availability (the same tap drives both records).
      qc.invalidateQueries({ queryKey: HR_KEYS.all });
      qc.invalidateQueries({ queryKey: ["check-in"] });
    },
  });

  return {
    ...mutation,
    punch: (type: HrPunchType) =>
      mutation.mutate({ type, idempotencyKey: newIdempotencyKey(), source: "WEB" }),
  };
}
